import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker, Button, Space, message, InputNumber, Switch, Card } from 'antd';
import { SaveOutlined, CloseOutlined, QrcodeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { eventApi } from '../services/eventApi';
import type { EventSession, CreateEventRequest, UpdateEventRequest } from '../types';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface EventFormProps {
  event?: EventSession | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const EventForm: React.FC<EventFormProps> = ({ event, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  useEffect(() => {
    if (event) {
      // Check if event is ended or if this is view mode
      const isEnded = event.status === 'ended' || new Date() > new Date(event.endTime);
      setIsViewMode(isEnded);

      form.setFieldsValue({
        eventName: event.eventName,
        location: event.location,
        timeRange: [dayjs(event.startTime), dayjs(event.endTime)],
        description: event.description,
        qrRefreshInterval: event.qrRefreshInterval,
        status: event.status
      });
    } else {
      // Set default values for new event
      form.setFieldsValue({
        qrRefreshInterval: 30,
        status: 'inactive'
      });
    }
  }, [event, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const [startTime, endTime] = values.timeRange;
      const eventData = {
        eventName: values.eventName,
        location: values.location,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        description: values.description,
        qrRefreshInterval: values.qrRefreshInterval
      };

      if (event) {
        // Update existing event
        const updateData: UpdateEventRequest = {
          ...eventData,
          status: values.status
        };
        await eventApi.updateEvent(event.id, updateData);
        message.success('Cập nhật sự kiện thành công');
      } else {
        // Create new event
        const createData: CreateEventRequest = eventData;
        await eventApi.createEvent(createData);
        message.success('Tạo sự kiện thành công');
      }

      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Có lỗi xảy ra';
      message.error(`${event ? 'Cập nhật' : 'Tạo'} sự kiện thất bại: ${errorMessage}`);
      console.error('Error saving event:', error);
    } finally {
      setLoading(false);
    }
  };

  const openQRDisplay = () => {
    if (event?.qrDisplayUrl) {
      window.open(event.qrDisplayUrl, '_blank');
    } else {
      message.warning('URL hiển thị QR chưa được tạo');
    }
  };

  const validateTimeRange = (_: any, value: any) => {
    if (!value || !value[0] || !value[1]) {
      return Promise.reject(new Error('Vui lòng chọn thời gian bắt đầu và kết thúc'));
    }

    const [startTime, endTime] = value;
    const now = dayjs();

    if (startTime.isBefore(now)) {
      return Promise.reject(new Error('Thời gian bắt đầu phải sau thời điểm hiện tại'));
    }

    if (endTime.isBefore(startTime.add(5, 'minute'))) {
      return Promise.reject(new Error('Thời gian kết thúc phải sau thời gian bắt đầu ít nhất 5 phút'));
    }

    return Promise.resolve();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      disabled={isViewMode}
    >
      {event && (
        <Card 
          size="small" 
          style={{ marginBottom: 16, backgroundColor: '#f6f8fa' }}
          title="Thông tin sự kiện"
          extra={
            event.status === 'active' && (
              <Button
                type="primary"
                icon={<QrcodeOutlined />}
                onClick={openQRDisplay}
                size="small"
              >
                Mở QR Display
              </Button>
            )
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
            <div><strong>ID:</strong> {event.id}</div>
            <div><strong>Trạng thái:</strong> {
              event.status === 'active' ? '🟢 Đang hoạt động' :
              event.status === 'inactive' ? '🟡 Tạm dừng' : '🔴 Đã kết thúc'
            }</div>
            <div><strong>Ngày tạo:</strong> {new Date(event.createdAt).toLocaleString('vi-VN')}</div>
            <div><strong>Cập nhật:</strong> {new Date(event.updatedAt).toLocaleString('vi-VN')}</div>
            {event.qrDisplayUrl && (
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>QR URL:</strong>{' '}
                <a href={event.qrDisplayUrl} target="_blank" rel="noopener noreferrer">
                  {event.qrDisplayUrl}
                </a>
              </div>
            )}
          </div>
        </Card>
      )}

      <Form.Item
        label="Tên sự kiện"
        name="eventName"
        rules={[
          { required: true, message: 'Vui lòng nhập tên sự kiện' },
          { min: 3, message: 'Tên sự kiện phải có ít nhất 3 ký tự' },
          { max: 255, message: 'Tên sự kiện không được quá 255 ký tự' }
        ]}
      >
        <Input placeholder="Nhập tên sự kiện" />
      </Form.Item>

      <Form.Item
        label="Địa điểm"
        name="location"
        rules={[
          { required: true, message: 'Vui lòng nhập địa điểm' },
          { min: 3, message: 'Địa điểm phải có ít nhất 3 ký tự' },
          { max: 500, message: 'Địa điểm không được quá 500 ký tự' }
        ]}
      >
        <Input placeholder="Nhập địa điểm tổ chức" />
      </Form.Item>

      <Form.Item
        label="Thời gian diễn ra"
        name="timeRange"
        rules={[{ validator: validateTimeRange }]}
      >
        <RangePicker
          showTime={{ format: 'HH:mm' }}
          format="DD/MM/YYYY HH:mm"
          placeholder={['Thời gian bắt đầu', 'Thời gian kết thúc']}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        label="Mô tả sự kiện"
        name="description"
        rules={[
          { max: 1000, message: 'Mô tả không được quá 1000 ký tự' }
        ]}
      >
        <TextArea
          rows={4}
          placeholder="Nhập mô tả chi tiết về sự kiện (tùy chọn)"
          showCount
          maxLength={1000}
        />
      </Form.Item>

      <Form.Item
        label="Thời gian cập nhật QR (giây)"
        name="qrRefreshInterval"
        rules={[
          { required: true, message: 'Vui lòng nhập thời gian cập nhật QR' },
          { type: 'number', min: 10, message: 'Thời gian tối thiểu là 10 giây' },
          { type: 'number', max: 300, message: 'Thời gian tối đa là 300 giây (5 phút)' }
        ]}
        tooltip="Thời gian mà mã QR sẽ được làm mới. Khuyến nghị: 30-60 giây"
      >
        <InputNumber
          min={10}
          max={300}
          style={{ width: '100%' }}
          placeholder="30"
          addonAfter="giây"
        />
      </Form.Item>

      {event && (
        <Form.Item
          label="Trạng thái"
          name="status"
          valuePropName="checked"
          getValueFromEvent={(checked) => checked ? 'active' : 'inactive'}
          getValueProps={(value) => ({ checked: value === 'active' })}
        >
          <Switch
            checkedChildren="Hoạt động"
            unCheckedChildren="Tạm dừng"
            disabled={event.status === 'ended'}
          />
        </Form.Item>
      )}

      <Form.Item style={{ marginBottom: 0 }}>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} icon={<CloseOutlined />}>
            {isViewMode ? 'Đóng' : 'Hủy'}
          </Button>
          {!isViewMode && (
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
            >
              {event ? 'Cập nhật' : 'Tạo sự kiện'}
            </Button>
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default EventForm;
